import type { AxiosInstance } from 'axios';

export type PlatformServiceStatus = 'HEALTHY' | 'DEGRADED' | 'DOWN';
export type PlatformServiceTier = 'CORE' | 'COMMERCE' | 'INTELLIGENCE' | 'EXTENSION';
export type ServiceEventStatus =
  | 'QUEUED'
  | 'DELIVERED'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'
  | 'SKIPPED';

export interface PlatformService {
  id: string;
  key: string;
  name: string;
  description: string | null;
  tier: PlatformServiceTier;
  status: PlatformServiceStatus;
  endpointPrefixes: string[];
  ownedTables: string[];
  emitsEvents: string[];
  consumesEvents: string[];
  version: string | null;
  lastHeartbeatAt: string | null;
  createdAt: string;
}

export interface ServiceEvent {
  id: string;
  kind: string;
  contentHash: string;
  sourceService: string;
  subjectId: string;
  payload: unknown;
  refType: string | null;
  refId: string | null;
  status: ServiceEventStatus;
  occurredAt: string;
  processedAt: string | null;
  error: string | null;
  attempts: number;
}

export interface AuditLogActor {
  name: string;
  email: string;
}

export interface AuditLog {
  id: string;
  actorId: string | null;
  actor?: AuditLogActor | null;
  agentId: string | null;
  actorRole: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  before: unknown | null;
  after: unknown | null;
  ip: string | null;
  requestId: string | null;
  createdAt: string;
}

export interface ListAuditLogsResponse {
  data: AuditLog[];
  page: {
    page: number;
    pageSize: number;
    total: number;
    pageCount: number;
  };
}

export function platformEndpoints(http: AxiosInstance) {
  return {
    listServices: () =>
      http.get<{ data: PlatformService[] }>('/admin/platform/services').then((r) => r.data.data),
    registerService: (body: {
      key: string;
      name: string;
      description?: string;
      tier: PlatformServiceTier;
      endpointPrefixes?: string[];
      ownedTables?: string[];
      emitsEvents?: string[];
      consumesEvents?: string[];
      version?: string;
    }) => http.put<PlatformService>('/admin/platform/services', body).then((r) => r.data),
    heartbeat: (body: { key: string; status: PlatformServiceStatus; version?: string }) =>
      http.post('/admin/platform/services/heartbeat', body).then((r) => r.data),
    publishEvent: (body: {
      kind: string;
      sourceService: string;
      subjectId: string;
      refType?: string;
      refId?: string;
      payload?: Record<string, unknown>;
    }) => http.post('/admin/platform/events', body).then((r) => r.data),
    listEvents: (params?: {
      kind?: string;
      sourceService?: string;
      subjectId?: string;
      status?: ServiceEventStatus;
      limit?: number;
    }) =>
      http
        .get<{ data: ServiceEvent[] }>('/admin/platform/events', { params })
        .then((r) => r.data.data),
    listAuditLogs: (params?: {
      page?: number;
      pageSize?: number;
      q?: string;
      action?: string;
      entityType?: string;
      actorId?: string;
      actorRole?: string;
      module?: string;
      ip?: string;
      startDate?: string;
      endDate?: string;
      exportCsv?: boolean;
    }) =>
      http.get<ListAuditLogsResponse>('/admin/platform/audit-logs', { params }).then((r) => r.data),
    pruneAuditLogs: (days: number) =>
      http
        .post<{ prunedCount: number }>('/admin/platform/audit-logs/retention', { days })
        .then((r) => r.data),
  };
}

export type PlatformEndpoints = ReturnType<typeof platformEndpoints>;
